import { observable, action } from 'mobx';
import fetchDataHelper from '../helpers/fetchDataHelper';
import toastNotificationHelper from '../helpers/toastNotificationHelper';

export interface ISettings {
	id: number;
	userName: string;
	telegramChatId: string;
	telegramBotToken: string;
	apiUrl: string;
	defaultQuote: string;
	symbols: string[];	// excluded symbols - do not rename
	volumeDefaultView: number;
	extendedRowVisible: boolean;
}

export interface ISettingsStore {
	settings: ISettings;
	inProgress: boolean;
	telegramEnabled: boolean;
	apiEnabled: boolean;
	defaultQuote: string;
	volumeDefaultView: number;
	excludedSymbols: string[];
	extendedRowVisible: boolean;
	adminPost: string;
	initSettings(): Promise<void>; 
	saveSettings(): Promise<void>; 
	testTelegramSettings(): Promise<void>;
	pushAdminPost(): Promise<void>;
}

class SettingsStore implements ISettingsStore {

	@observable settings: ISettings;
	@observable inProgress: boolean = false;
	@observable telegramEnabled: boolean = false;
	@observable apiEnabled: boolean = false;
	@observable defaultQuote: string = 'N/A';
	@observable volumeDefaultView: number = 2;
	@observable excludedSymbols: string[] = [];
	@observable extendedRowVisible: boolean = false;
	@observable adminPost: string = '';

	@action.bound setValues(): void {
		if (!this.settings) return;

		this.telegramEnabled = !this.isNullOrEmpty(this.settings.telegramBotToken) && !this.isNullOrEmpty(this.settings.telegramChatId);
		this.apiEnabled = !this.isNullOrEmpty(this.settings.apiUrl);
		this.defaultQuote = this.settings.defaultQuote;
		this.volumeDefaultView = this.settings.volumeDefaultView;
		this.excludedSymbols = this.settings.symbols;
		this.extendedRowVisible = this.settings.extendedRowVisible;
	}

	@action initSettings(): Promise<void> {
		this.inProgress = true;

		return fetchDataHelper.getReturnJson("/settings/getSettings")
			.then(
				(result) => {
					this.settings = result;
					this.setValues();
					
					this.inProgress = false;
					return Promise.resolve();
				},
				(error) => {
					toastNotificationHelper.notifyError('Init settings failed'); 
					console.log('Init settings failed: ' + error);        
					this.inProgress = false;
					return Promise.reject();
				}
			);
	}

	@action saveSettings(): Promise<void> {
		var isValid = this.validateSettings();
		if (!isValid){
			return Promise.reject();
		}

		this.inProgress = true;

		return fetchDataHelper.post("/settings/saveSettings", this.settings)
			.then(
				(result) => {
					this.initSettings();
					
					this.inProgress = false;
					return Promise.resolve();
				},
				(error) => {
					toastNotificationHelper.notifyError('Save settings failed');
					console.log('Save settings failed: ' + error);
					this.inProgress = false;
					return Promise.reject();
				}
			);
	}

	@action.bound testTelegramSettings(): Promise<void> {
		var isValid = this.validateSettings();
		if (!isValid){
			return Promise.reject();
		}

		this.inProgress = true;

		var request = {
			telegramBotToken: this.settings.telegramBotToken,
			telegramChatId: this.settings.telegramChatId
		}

		return fetchDataHelper.post("/settings/testTelegramSettings", request)
			.then(
				() => {
					this.inProgress = false;
					return Promise.resolve();
				},
				(error) => {
					toastNotificationHelper.notifyError('Test Telegram settings failed');
					console.log('Test Telegram settings failed: ' + error);
					this.inProgress = false;
					return Promise.reject();
				}
			);
	}

	validateSettings(): boolean {
		var isValid = true;

		if ((this.settings.telegramBotToken && !this.settings.telegramChatId) || (!this.settings.telegramBotToken && this.settings.telegramChatId)) {
			toastNotificationHelper.notifyError('Telegram bot API token or Telegram chat ID not provided');
			isValid = false;
		}
	
		if (this.settings.apiUrl && !this.settings.apiUrl.startsWith('http://') && !this.settings.apiUrl.startsWith('https://')) {
			toastNotificationHelper.notifyError('API Url is not valid');
			isValid = false;
		}

		return isValid;
	}

	isNullOrEmpty(value: string): boolean {
		if (value == undefined || value == null) return true;

		return value == '';
	}

	@action.bound pushAdminPost(): Promise<void> {

		var request = {
			title: this.adminPost
		};

		return fetchDataHelper.post("/maintenance/pushAdminPost", request)
		.then(
			(result) => {
				this.adminPost = '';
				return Promise.resolve();
			},
			(error) => {
				toastNotificationHelper.notifyError('Push admin post failed');				
				return Promise.reject();
			}
		);
	}
}

export default new SettingsStore();